'use client';
import type { Prettify } from '@mnd-frontend/ts';
import {
  RADIUS_XL,
  SIZING_2_5,
  SIZING_5,
  SPACING_0_5,
  SPACING_3,
  SPACING_5,
  WEB_GREY_LT,
  WEB_GREY_REG,
  WEB_PURPLE_EXTRA_LIT,
  WEB_PURPLE_LT,
  WEB_PURPLE_MID,
  WEB_PURPLE_REG,
  WEB_RED_EXTRA_LIT,
  WEB_RED_LT,
  WEB_RED_MID,
  WEB_RED_REG,
  WEB_WHITE_REG,
} from '@mnd-frontend/ui/shared/tokens';
import Link from 'next/link';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react';
import styled, { css } from 'styled-components';
import Spinner from '../Spinner';

export const configurationPropValues = [
  'primary',
  'secondary',
  'tertiary',
  'ghost',
  'ghostTransparent',
  'ghostRed',
] as const;

export type Configuration = (typeof configurationPropValues)[number];

const ButtonContent = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${SPACING_0_5};
  height: 100%;
`;

const IconWrapper = styled.span`
  line-height: 0;
  flex-shrink: 0;
  width: ${SIZING_2_5};
  height: ${SIZING_2_5};
  > * {
    width: 100%;
    height: 100%;
  }
`;

const ButtonChildrenWrapper = styled.span`
  overflow: hidden;
  font-weight: 700;
  text-overflow: ellipsis;
`;

const SpinnerWrapper = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  line-height: 0;
  transform: translate(-50%, -50%);
`;

interface ButtonStyleProps {
  $configuration: Configuration;
  $loading?: boolean;
  $iconOnly?: boolean;
  $fullWidth?: boolean;
}

const ButtonStyles = styled('button')<ButtonStyleProps>`
  cursor: pointer;
  font-weight: 600;
  max-width: 100%;
  padding: ${({ $iconOnly, $fullWidth }) => ($iconOnly || $fullWidth ? 0 : `0 ${SPACING_3}`)};
  border-radius: ${RADIUS_XL};
  font-size: 1rem;
  font-family: inherit;
  outline: 0;
  text-decoration: none;
  position: relative;
  border: 0;
  line-height: normal;
  white-space: nowrap;
  height: ${SIZING_5};
  width: ${({ $iconOnly, $fullWidth }) => ($iconOnly ? SPACING_5 : $fullWidth ? '100%' : 'fit-content')};
  flex-shrink: 0;
  display: inline-block;
  * {
    /* Used to make sure target.tagName of events will be BUTTON and not span. */
    pointer-events: none;
  }
  ${ButtonChildrenWrapper} {
    font-weight: ${({ $configuration }) => (['ghost', 'ghostTransparent'].includes($configuration) ? 500 : 600)};
    line-height: ${({ $iconOnly }) => $iconOnly && 0};
  }
  ${ButtonChildrenWrapper},
  ${IconWrapper} {
    opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  }
  [data-whatintent='keyboard'] &:focus,
  &:hover {
    color: var(--button-hover-color);
    background: var(--button-hover-bgcolor);
  }
  &[aria-disabled='true'],
  &:disabled {
    pointer-events: none;
    cursor: default;
  }
  ${({ $configuration }) => {
    switch ($configuration) {
      case 'primary':
        return css`
          color: ${WEB_WHITE_REG};
          background: ${WEB_RED_REG};
          --spinner-color: ${WEB_GREY_REG};
          --button-hover-color: ${WEB_WHITE_REG};
          --button-hover-bgcolor: ${WEB_RED_MID};
          &:focus-visible {
            outline: 3px solid ${WEB_PURPLE_EXTRA_LIT};
          }
          &[aria-disabled='true'],
          &:disabled {
            color: ${WEB_GREY_REG};
            background: ${WEB_GREY_LT};
          }
        `;
      case 'secondary':
        return css`
          color: #ffffff;
          background: ${WEB_PURPLE_REG};
          --spinner-color: ${WEB_GREY_REG};
          --button-hover-color: ${WEB_WHITE_REG};
          --button-hover-bgcolor: ${WEB_PURPLE_MID};
          &:focus-visible {
            outline: 3px solid ${WEB_PURPLE_EXTRA_LIT};
          }
          &[aria-disabled='true'],
          &:disabled {
            color: ${WEB_GREY_REG};
            background: ${WEB_GREY_LT};
          }
        `;
      case 'tertiary':
        return css`
          color: ${WEB_RED_REG};
          background: ${WEB_WHITE_REG};
          --spinner-color: ${WEB_RED_EXTRA_LIT};
          --button-hover-color: ${WEB_RED_REG};;
          --button-hover-bgcolor: ${WEB_RED_EXTRA_LIT};
          &:focus-visible {
            outline: 3px solid ${WEB_PURPLE_EXTRA_LIT};
          }
          &[aria-disabled='true'],
          &:disabled {
            background: ${WEB_WHITE_REG};
            color: ${WEB_RED_EXTRA_LIT};
          }
        `;
      case 'ghost':
        return css`
          color: ${WEB_PURPLE_REG};
          background: transparent;
          border: 1px solid ${WEB_PURPLE_REG};
          --spinner-color: ${WEB_GREY_REG};
          --button-hover-color: ${WEB_PURPLE_LT};
          --button-hover-bgcolor: transparent;
          &:hover {
            border-color: ${WEB_PURPLE_LT};
          }
          &:focus-visible {
            outline: 3px solid ${WEB_PURPLE_EXTRA_LIT};
          }
          &[aria-disabled='true'],
          &:disabled {
            border-color: ${WEB_GREY_REG};
            color: ${WEB_GREY_REG};
          }
        `;
      case 'ghostTransparent':
        return css`
          color: ${WEB_WHITE_REG};
          background: transparent;
          border: 1px solid ${WEB_WHITE_REG};
          --spinner-color: ${WEB_GREY_REG};
          --button-hover-color: ${WEB_RED_LT};
          --button-hover-bgcolor: transparent;
          &:hover {
            border-color: ${WEB_RED_LT};
          }
          &:focus-visible {
            outline: 3px solid ${WEB_GREY_LT};
          }
          &[aria-disabled='true'],
          &:disabled {
            border-color: ${WEB_GREY_REG};
            color: ${WEB_GREY_REG};
          }
        `;
      case 'ghostRed':
        return css`
          color: ${WEB_RED_REG};
          background: transparent;
          border: 1px solid ${WEB_RED_REG};
          --spinner-color: ${WEB_GREY_REG};
          --button-hover-color: ${WEB_RED_MID};
          --button-hover-bgcolor: transparent;
          &:hover {
            border-color: ${WEB_RED_MID};
          }
          &:focus-visible {
            outline: 3px solid ${WEB_PURPLE_EXTRA_LIT};
          }
          &[aria-disabled='true'],
          &:disabled {
            border-color: ${WEB_GREY_REG};
            color: ${WEB_GREY_REG};
          }
        `;
    }
  }}
`;

interface CommonButtonProps {
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  disabled?: boolean;
  id?: string;
  loading?: boolean;
  configuration?: Configuration;
  fullWidth?: boolean;
}

type ChildrenContent =
  | { icon: ReactNode; 'aria-label': string; children?: undefined }
  | { children: string | number; icon?: undefined };

type ButtonAsButton = {
  type: 'button' | 'submit';
  href?: never;
  newTab?: never;
  replace?: never;
  download?: never;
  prefetch?: never;
} & CommonButtonProps &
  ButtonHTMLAttributes<HTMLButtonElement>;

type ButtonAsLink = {
  type?: never;
  href: string;
  newTab?: boolean;
  replace?: boolean;
  download?: string;
  prefetch?: boolean;
} & CommonButtonProps &
  AnchorHTMLAttributes<HTMLAnchorElement>;

export type ButtonProps = Prettify<ButtonAsButton | ButtonAsLink>;

const Button = ({
  leadingIcon,
  trailingIcon,
  disabled = false,
  loading = false,
  configuration = 'primary',
  href,
  newTab = false,
  download,
  replace = false,
  onClick,
  type = 'button',
  prefetch = false,
  ref,
  icon,
  fullWidth,
  ...restProps
}: ButtonProps &
  ChildrenContent & {
    ref?: Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>;
  }) => {
  const functionallyDisabled = disabled || loading;
  const protocolRegex = /^[a-z]{1,10}:/;

  const isExternalLink = href && (newTab || protocolRegex.test(href));
  const isInternalLink = href && !isExternalLink;

  const commonProps = {
    'aria-disabled': functionallyDisabled,
    tabIndex: functionallyDisabled ? -1 : undefined,
    'aria-label': 'aria-label' in restProps ? restProps['aria-label'] : undefined,
  };

  const content = (
    <ButtonContent as="div">
      {leadingIcon && <IconWrapper>{leadingIcon}</IconWrapper>}
      <ButtonChildrenWrapper>{icon ? icon : restProps.children}</ButtonChildrenWrapper>
      {trailingIcon && <IconWrapper>{trailingIcon}</IconWrapper>}
    </ButtonContent>
  );

  const spinner = loading && (
    <SpinnerWrapper>
      <Spinner size="sm" />
    </SpinnerWrapper>
  );

  if (isInternalLink)
    return (
      <Link
        href={href}
        prefetch={prefetch}
        replace={replace}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        ref={ref as Ref<HTMLAnchorElement>}
        {...commonProps}
        {...(restProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
        style={{ pointerEvents: functionallyDisabled ? 'none' : 'auto' }}
      >
        <ButtonStyles
          as="div"
          $configuration={configuration}
          $fullWidth={fullWidth}
          $iconOnly={!!icon}
          {...commonProps}
        >
          {content}
        </ButtonStyles>
      </Link>
    );

  if (isExternalLink)
    return (
      <ButtonStyles
        as="a"
        href={href}
        ref={ref as Ref<HTMLAnchorElement>}
        $configuration={configuration}
        $fullWidth={fullWidth}
        download={download}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        target={newTab ? '_blank' : undefined}
        rel="noreferrer noopener"
        $iconOnly={!!icon}
        {...commonProps}
        {...(restProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </ButtonStyles>
    );

  return (
    <ButtonStyles
      as="button"
      ref={ref as Ref<HTMLButtonElement>}
      type={type}
      $configuration={configuration}
      $fullWidth={fullWidth}
      disabled={functionallyDisabled}
      onClick={onClick as MouseEventHandler<HTMLButtonElement>}
      $loading={loading}
      aria-busy={loading}
      $iconOnly={!!icon}
      {...commonProps}
      {...(restProps as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
      {spinner}
    </ButtonStyles>
  );
};

export default Button;
